<template>
  <div class="wrapper">
    <v-card
      tile
      class="overflow-hidden no-scroll-bar"
      elevation="0"
      style="background-color: transparent;"
    >

      <v-app-bar
        class="app_bar"
        fixed
        color="primary"
        dark
        scroll-target="#scrolling-techniques-2"
      >
        <v-btn
          @click="toPrevMonth"
          :icon="locale === 'ja'"
          :variant="isEnglishOrKhmer ? 'text' : undefined"
          class="no-background-hover"
          :class="isEnglishOrKhmer ? 'calendar-nav-btn px-0 custom-v-btn' : ''"
          color="white"
          :dark="locale === 'ja'"
        >
          <div class="d-flex align-center" :class="isEnglishOrKhmer ? 'calendar-nav-btn__inner' : ''">
            <v-icon size="20" :class="isEnglishOrKhmer ? 'calendar-nav-btn__icon' : locale === 'ja' ? 'ml-4' : ''">mdi-chevron-left</v-icon>
            <template v-if="locale === 'ja'">
              <h4 class="white--text">前の月</h4>
            </template>
            <template v-else>
              <span class="calendar-nav-btn__label">{{ translate('calendar.prevMonth') }}</span>
            </template>
          </div>
        </v-btn>

        <v-spacer></v-spacer>

        <v-app-bar-title :class="isEnglishOrKhmer ? 'calendar-app-bar-title' : ''">
          <div class="calendar-header-title-block" @click="isCalendar = !isCalendar">
            <h5 class="text-center" :class="isEnglishOrKhmer ? 'calendar-header-year' : ''">
              {{ locale === 'ja' ? year + '年' : year }}
            </h5>
            <div class="d-flex justify-center align-center">
              <h3>{{ month }}</h3>
              <v-icon v-if="isCalendar">mdi-chevron-down</v-icon>
              <v-icon v-else>mdi-chevron-up</v-icon>
            </div>
          </div>
        </v-app-bar-title>

        <v-spacer></v-spacer>

        <v-btn
          @click="toNextMonth"
          :icon="locale === 'ja'"
          :variant="isEnglishOrKhmer ? 'text' : undefined"
          class="no-background-hover"
          :class="isEnglishOrKhmer ? 'calendar-nav-btn px-0 custom-v-btn' : ''"
          color="white"
        >
          <div class="d-flex align-center" :class="isEnglishOrKhmer ? 'calendar-nav-btn__inner' : ''">
            <template v-if="locale === 'ja'">
              <h4 class="white--text">次の月</h4>
              <v-icon size="20" class="mr-4">mdi-chevron-right</v-icon>
            </template>
            <template v-else>
              <span class="calendar-nav-btn__label">{{ translate('calendar.nextMonth') }}</span>
              <v-icon size="20" class="calendar-nav-btn__icon">mdi-chevron-right</v-icon>
            </template>
          </div>
        </v-btn>

      </v-app-bar>
      <v-expand-transition>
        <v-sheet
          class="mt-8"
          v-if="isCalendar"
          height="400"
          style="background-color: transparent;"
        >
          <v-calendar
            ref="event_calendar"
            :now="now"
            v-model="selectedDate"
            color="primary"
            :locale="vCalendarLocale"
            :weekday-format="calendarWeekdayLabel"
            :day-format="calendarDayLabelSecondPart"
            :month-format="timestamp => new Date(timestamp.date).getDate() + ' /'"
            @click:date="viewDay"
          >
            <template v-slot:day="{ date }">
              <div
                v-for="e in getEventNum(date)"
                :key="e.eventId"
                class="d-flex justify-center"
              >
                <div class="event_num ma-1"></div>
              </div>
            </template>
          </v-calendar>
        </v-sheet>
      </v-expand-transition>
      <div
        v-show="!isCalendar"
        class="pa-7"
      >
      </div>

      <v-sheet
        id="scrolling-techniques-2"
        class="overflow-y-auto no-scroll-bar"
        :style="{
          maxHeight: `calc(100vh - var(--app-top-offset) - 60px - 56px - env(safe-area-inset-bottom))`,
          paddingBottom: 'calc(56px + env(safe-area-inset-bottom))',
          backgroundColor: 'transparent'
        }"
        elevation="0"
      >
        <div
          v-scroll:#scrolling-techniques-2="onScroll"
          v-for="(e, index) in events"
          :key="e + index"
          class="plan-wrapper ma-1 pa-2 pr-3 d-flex"
        >
          <div class="text-center pr-2" :style="isEnglishOrKhmer ? 'width: 50px;' : 'width: 40px;'">
            <p class="caption">{{ dayFormat(e.date) }}</p>
            <h4 class="text-no-wrap">{{ dateFormat(e.date) }}</h4>
          </div>
          <div class="plan-content">
            <div class="event-name">
              <p
                v-for="(name, nameIndex) in getEventNames(e.eventName)"
                :key="nameIndex"
                class="event-name-item"
              >
              {{ name }}
            </p>
            </div>
            <div class="children-wrap">
              <div
                v-for="child in e.children"
                :key="child.childId"
                class="icon"
              >
                <img
                  :src="resolveChildImageUrl(child.imageUrl) || 'https://via.placeholder.com/240x180?text=Sample+Image+1'"
                >
              </div>
            </div>
          </div>
        </div>
      </v-sheet>

    </v-card>
  </div>
</template>
<script lang="ts">
import Vue from 'vue';
import { defineComponent } from 'vue';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import 'dayjs/locale/en';
import 'dayjs/locale/km';
import {
  locale as appLocale,
  t as translate,
  dayjsLocale as appDayjsLocale,
  calendarLocale,
  isEnglishOrKhmerLocale,
  isJapaneseLocale,
  isKhmerLocale,
} from '@/i18n';

dayjs.locale(appDayjsLocale);
import { when } from '@/service/Switch';

import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import { VCalendar } from '@/model/Vuetify/VCalendar';

import { eventsModule } from '@/store/entityModules/events';
import { familyModule } from '@/store/entityModules/family';
import { resolveChildImageUrl } from '@/utils/childImageUrl';

import { ChildResponse } from '@API/src/component/home/family/types';
import {
  DefecationStatus,
  DefecationStatusType,
} from '../../node_modules/chaild-entity/src/component/home/defecation/types';

interface Event {
  name: string;
  date: string;
  start: Date;
  end: Date;
  color: string;
  timed: boolean;
  children: ChildResponse[];
}

interface DataType {
  selectedDate: string;
  selectedEvents: Event[];
  scrollInvoked: number;
  isCalendar: boolean;
  fetchLoading: boolean;
}

export default defineComponent({

  data: (): DataType =>({
    selectedDate: dayjs().format('YYYY-MM-DD'),
    selectedEvents: [],
    scrollInvoked: 0,
    isCalendar: true,
    fetchLoading: false,
  }),

  computed: {
    locale() {
      return appLocale;
    },
    isEnglishOrKhmer(): boolean {
      return isEnglishOrKhmerLocale(this.locale);
    },
    vCalendarLocale(): string {
      return calendarLocale(this.locale);
    },
    calendar(): VCalendar {
      return (this.$refs as any).event_calendar;
    },
    events() {
      return eventsModule.eventsIncludedDate;
    },
    children() {
      if (familyModule.family) {
        return familyModule.family.children;
      }
      return [];
    },
    now(): string {
      return dayjs(this.selectedDate).format('YYYY-MM-DD');
    },
    year(): string {
      if (isJapaneseLocale(this.locale)) {
        return dayjs(this.selectedDate).format('YYYY');
      }
      return translate('record.date.yearSuffix', '{year}', {
        year: dayjs(this.selectedDate).format('YYYY'),
      });
    },
    month(): string {
      return translate('calendar.monthName', '{month}', {
        month: dayjs(this.selectedDate).locale(this.locale).format('MMMM'),
      });
    },
    today(): string {
      if (isJapaneseLocale(this.locale)) {
        return dayjs(this.selectedDate).format('M月D日');
      }
      return translate('record.date.monthDay', '{month}/{day}', {
        month: dayjs(this.selectedDate).format('M'),
        day: dayjs(this.selectedDate).format('D'),
      });
    },
    dayOfTheWeek(): string {
      return dayjs(this.selectedDate).locale(this.locale).format('ddd');
    },
    dateFormatted(): string {
      const m = dayjs(this.selectedDate);
      const year = m.format('YYYY');
      const month = m.format('M');
      const day = m.format('D');
      const dayOfWeek = m.format('ddd');
      if (isJapaneseLocale(this.locale)) {
        return m.format('YYYY年MM月DD日（ddd）');
      }
      return translate('calendar.dateFormatFull', '{year}/{month}/{day} ({dayOfWeek})', { year, month, day, dayOfWeek });
    },
  },

  methods: {
    resolveChildImageUrl,
    calendarWeekdayLabel(timestamp: { date: string }): string {
      if (isKhmerLocale(this.locale)) {
        const khmerWeekdays = ['អាទិ', 'ចន្ទ', 'អង្គារ', 'ពុធ', 'ព្រហ', 'សុក្រ', 'សៅរ៍'];
        return khmerWeekdays[new Date(timestamp.date).getDay()];
      }
      return dayjs(timestamp.date).locale(this.locale).format('ddd');
    },
    /** VCalendar ghép monthFormat + ' ' + dayFormat khi ngày 1; các ngày khác chỉ dùng dayFormat (phải là số ngày). */
    calendarDayLabelSecondPart(timestamp: { date: string; day: number }): string {
      const d = new Date(timestamp.date);
      if (timestamp.day === 1) {
        return String(d.getMonth() + 1);
      }
      return String(d.getDate());
    },
    translate(key: string, fallback = key, params: Record<string, string | number> = {}): string {
      return translate(key, fallback, params);
    },
    toNextMonth() {
      this.selectedDate = dayjs(this.selectedDate).add(1, 'month').startOf('month').format('YYYY-MM-DD');

      sessionStorage.setItem('calendarYearMonth', this.selectedDate);
      this.fetchEvents();
    },

    toPrevMonth() {
      this.selectedDate = dayjs(this.selectedDate).subtract(1, 'month').startOf('month').format('YYYY-MM-DD');

      sessionStorage.setItem('calendarYearMonth', this.selectedDate);
      this.fetchEvents();
    },
    viewDay({ date }: { date: string }) {
      this.selectedDate = date;
    },
    onScroll(e) {
      this.scrollInvoked = e.target.scrollTop;
      if (this.scrollInvoked > 0) {
        this.isCalendar = false;
      } else {
        this.isCalendar = true;
      }
    },
    minuteFormat(iso: string) {
      return dayjs(iso).format('HH:mm');
    },
    dateFormat(iso: string) {
      return dayjs(iso).format('D');
    },
    dayFormat(iso: string) {
      return dayjs(iso).locale(this.locale).format('ddd');
    },
    getDefecationState(state: DefecationStatusType): string {
      return when(state)
        .on((v) => v === DefecationStatus.hard, () => translate('calendar.defecation.hard'))
        .on((v) => v === DefecationStatus.normal, () => translate('calendar.defecation.normal'))
        .on((v) => v === DefecationStatus.soft, () => translate('calendar.defecation.soft'))
        .on((v) => v === DefecationStatus.diarrhea, () => translate('calendar.defecation.diarrhea'))
        .otherwise(() => '');
    },
    async fetchEvents() {
      this.fetchLoading = true;
      const fromDate = dayjs(this.selectedDate).startOf('month');
      const endDate = dayjs(this.selectedDate).endOf('month');
      await eventsModule.fetchEvents({
        childId: null,
        from: dayjs(fromDate).format('YYYY-MM-DD'),
        to: dayjs(endDate).format('YYYY-MM-DD'),
      });
      this.fetchLoading = false;
    },
    showEvent({ nativeEvent, event }) {
      this.selectedEvents.push(event);
    },
    formatSelectedEvents(): string[] {
      const events: string[] = [];
      if (this.selectedEvents.length) {
        this.selectedDate = dayjs(this.selectedEvents[0].start).format('YYYY-MM-DD');
        for (const s of this.selectedEvents) {
          if (
            dayjs(s.start).format('hh:mm') === '12:00' &&
            dayjs(s.end).format('hh:mm') === '12:00'
          ) {
            events.push(`${s.name}`);
          } else if (s.start && s.end) {
            const from = dayjs(s.start).format('hh:mm');
            const to = dayjs(s.end).format('hh:mm');
            events.push(`${from}-${to} ${s.name}`);
          }
        }
        return events;
      }
      return [];
    },
    getEventNum(date: string) {
      const arr = this.events.filter((e) => {
        if (e.date === date) { return e; }
      });
      return arr;
    },
    getEventNames(eventName: string): string[] {
      return eventName.split('\n').map((name) => name.trim()).filter(Boolean);
    },
  },

  async mounted() {
    const calendarYearMonth = sessionStorage.getItem('calendarYearMonth');
    if (calendarYearMonth) this.selectedDate = calendarYearMonth;
    this.fetchEvents();

  },
});
</script>
<style lang="scss" scoped>
@use '../sass/ress.scss';

.wrapper {
  height: 100%;
  overflow-y: scroll;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;

  .app_bar {
    @supports (bottom: env(safe-area-inset-bottom)) {
      padding-top: env(safe-area-inset-top);
    }

    :deep(.custom-v-btn) {
      letter-spacing: 0 !important;
    }

    :deep(.calendar-nav-btn__label) {
      font-size: 0.8125rem;
      font-weight: 600;
      line-height: 1.2;
      white-space: nowrap;
    }

    :deep(.calendar-nav-btn__icon) {
      flex-shrink: 0;
    }

    :deep(.calendar-app-bar-title) {
      flex: 1 1 auto;
      min-width: 0;
      max-width: 100%;
      margin-inline: 4px;
      overflow: hidden;
    }

    :deep(.calendar-header-year) {
      line-height: 1.2;
    }

    @media (max-width: 320px) {
      :deep(.calendar-header-title-block) {
        h5 {
          font-size: calc(1.25rem - 10px);
        }

        h3 {
          font-size: calc(1.5rem - 10px);
        }

        .v-icon {
          font-size: calc(24px - 5px) !important;
        }
      }
    }

    :deep(.calendar-header-month) {
      font-size: clamp(0.875rem, 3vw, 1.25rem);
      line-height: 1.2;
      white-space: nowrap;
    }
  }

  .plan-wrapper {
    min-height: 58px;
    border: 1px solid rgb(var(--v-theme-success));
    border-radius: 5px 5px;
    align-items: flex-start;
  }

  .plan-content {
    display: flex;
    align-items: flex-start;
    align-self: stretch;
    gap: 4px;
    flex-wrap: nowrap;
    flex: 1;
    min-width: 0;
  }

  .event-name {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-self: center;
    gap: 10px;
    white-space: normal;
    user-select: none;
    flex: 0 1 50%;
    max-width: 50%;
    min-width: 0;
    word-break: normal;
    overflow-wrap: normal;
  }

  .event-name-item {
    margin: 0;
    padding: 0;
    line-height: 1.1;
  }

  .children-wrap {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 4px;
    margin-left: auto;
    flex: 0 1 50%;
    max-width: 50%;
  }

  .icon {
    height: 40px;
    width: 40px;

    img {
      height: 100%;
      width: 100%;
      border-radius: 100%;
      object-fit: cover;
    }
  }

  .event_num {
    height: 8px;
    width: 8px;
    background-color: rgb(var(--v-theme-primary));
    border-radius: 100%;
  }
}
</style>
